import React, { useEffect, useId, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const toValue = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
// value: 'YYYY-MM-DD' или 'YYYY-MM-DD HH:mm' — для календаря берём только дату.
const parse = (value) => value ? new Date(`${String(value).slice(0, 10)}T00:00:00`) : new Date();
// Время 'HH:mm' из value; у date-only значений времени нет.
const timeOf = (value) => (typeof value === 'string' && value.match(/[T ](\d{2}:\d{2})/) ? value.match(/[T ](\d{2}:\d{2})/)[1] : '');
const sameDay = (left, right) => toValue(left) === toValue(right);

const DatePicker = ({ label, name, value, onChange, required, error }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => parse(value));
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const dayRefs = useRef(new Map());
  const labelId = useId();
  const calendarId = useId();
  const errorId = `${calendarId}-error`;
  const selectedDate = value ? parse(value) : null;
  const [focusedDate, setFocusedDate] = useState(() => selectedDate || new Date());
  useEffect(() => { const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener('mousedown', close); return () => document.removeEventListener('mousedown', close); }, []);
  useEffect(() => {
    if (open) dayRefs.current.get(toValue(focusedDate))?.focus();
  }, [focusedDate, open, view]);
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const start = (first.getDay() + 6) % 7;
  const days = Array.from({ length: 42 }, (_, i) => new Date(view.getFullYear(), view.getMonth(), i - start + 1));
  const selected = value || '';
  const time = timeOf(value);
  const dateLabel = value ? parse(value).toLocaleDateString('ru-RU') : 'Выберите дату';
  const openCalendar = () => {
    const date = selectedDate || new Date();
    setView(new Date(date.getFullYear(), date.getMonth(), 1));
    setFocusedDate(date);
    setOpen(true);
  };
  const closeCalendar = (returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  // Выбор дня сохраняет введённое время (PERS2-79 AC-4); для date-only
  // значений (старые операции) время не добавляется (AC-5).
  const choose = (date) => {
    onChange({ target: { name, value: time ? `${toValue(date)} ${time}` : toValue(date) } });
    closeCalendar(true);
  };
  // Изменение времени меняет только часть HH:mm, дата сохраняется.
  const handleTimeChange = (e) => {
    const base = selectedDate ? toValue(selectedDate) : toValue(new Date());
    onChange({ target: { name, value: `${base} ${e.target.value}` } });
  };
  const moveFocus = (date) => {
    setFocusedDate(date);
    setView(new Date(date.getFullYear(), date.getMonth(), 1));
  };
  const handleTriggerKeyDown = (event) => {
    if (['Enter', ' ', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      openCalendar();
    }
    if (event.key === 'Escape') closeCalendar(true);
  };
  const handleCalendarKeyDown = (event) => {
    let nextDate;
    if (event.key === 'ArrowLeft') nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), focusedDate.getDate() - 1);
    if (event.key === 'ArrowRight') nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), focusedDate.getDate() + 1);
    if (event.key === 'ArrowUp') nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), focusedDate.getDate() - 7);
    if (event.key === 'ArrowDown') nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), focusedDate.getDate() + 7);
    if (event.key === 'Home') nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), focusedDate.getDate() - ((focusedDate.getDay() + 6) % 7));
    if (event.key === 'End') nextDate = new Date(focusedDate.getFullYear(), focusedDate.getMonth(), focusedDate.getDate() + (6 - ((focusedDate.getDay() + 6) % 7)));
    if (event.key === 'PageUp') nextDate = new Date(focusedDate.getFullYear() - (event.shiftKey ? 1 : 0), focusedDate.getMonth() - (event.shiftKey ? 0 : 1), focusedDate.getDate());
    if (event.key === 'PageDown') nextDate = new Date(focusedDate.getFullYear() + (event.shiftKey ? 1 : 0), focusedDate.getMonth() + (event.shiftKey ? 0 : 1), focusedDate.getDate());
    if (nextDate) { event.preventDefault(); moveFocus(nextDate); }
    if (['Enter', ' '].includes(event.key)) { event.preventDefault(); choose(focusedDate); }
    if (event.key === 'Escape') { event.preventDefault(); closeCalendar(true); }
  };
  return <div ref={ref} className="relative w-full">
    {label && <label id={labelId} className="mb-2 block text-xs uppercase tracking-[0.24em] text-muted">{label}</label>}
    <div className="flex items-center gap-2">
      <button ref={triggerRef} type="button" onClick={() => open ? closeCalendar() : openCalendar()} onKeyDown={handleTriggerKeyDown} aria-labelledby={label ? labelId : undefined} aria-describedby={error ? errorId : undefined} aria-haspopup="dialog" aria-expanded={open} aria-controls={calendarId} className={`flex w-full flex-1 items-center justify-between rounded-2xl border border-line bg-surface px-4 py-3 text-left text-base text-ink outline-none focus:ring-2 focus:ring-accent ${error ? '!border-red-500 !bg-red-500/5 !ring-2 !ring-red-500/30' : ''}`}>
        <span>{dateLabel}</span><CalendarDays size={16} className="text-muted" />
      </button>
      <input
        type="time"
        aria-label="Время"
        aria-describedby={error ? errorId : undefined}
        value={time}
        onChange={handleTimeChange}
        className={`rounded-2xl border border-line bg-surface px-3 py-3 text-base text-ink outline-none focus:ring-2 focus:ring-accent ${error ? '!border-red-500 !bg-red-500/5' : ''}`}
      />
    </div>
    {open && <div id={calendarId} role="dialog" aria-label="Выбор даты" onKeyDown={handleCalendarKeyDown} className="absolute z-50 mt-2 w-72 rounded-2xl border border-line bg-surface p-4 shadow-glass">
      <div className="mb-3 flex items-center justify-between"><button type="button" aria-label="Предыдущий месяц" onClick={() => moveFocus(new Date(view.getFullYear(), view.getMonth() - 1, 1))}><ChevronLeft size={18}/></button><b aria-live="polite" className="text-sm">{months[view.getMonth()]} {view.getFullYear()}</b><button type="button" aria-label="Следующий месяц" onClick={() => moveFocus(new Date(view.getFullYear(), view.getMonth() + 1, 1))}><ChevronRight size={18}/></button></div>
      <div role="grid" aria-label={`${months[view.getMonth()]} ${view.getFullYear()}`} className="grid grid-cols-7 gap-1 text-center text-xs">{weekdays.map((day) => <span key={day} role="columnheader" className="py-1 text-muted">{day}</span>)}{days.map((day) => { const current = day.getMonth() === view.getMonth(); const active = toValue(day) === selected; const focused = sameDay(day, focusedDate); return <button ref={(element) => { if (element) dayRefs.current.set(toValue(day), element); else dayRefs.current.delete(toValue(day)); }} key={day.toISOString()} role="gridcell" tabIndex={focused ? 0 : -1} aria-selected={active} aria-label={`Выбрать ${day.toLocaleDateString('ru-RU')}`} type="button" onClick={() => choose(day)} className={`rounded-xl py-2 ${active ? 'bg-accent text-white' : current ? 'text-ink hover:bg-base' : 'text-muted'}`}>{day.getDate()}</button>; })}</div>
    </div>}
    {required && <input className="sr-only" tabIndex="-1" value={value} readOnly required />}
    {error && <p id={errorId} role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
  </div>;
};
export default DatePicker;
